import React from 'react';
import { Link, useLocation } from 'react-router';
import styled from 'styled-components';
import { CLUSTERS, getClusterPeers, getSeoPageMeta } from 'shared/consts/seoClusters';

const Wrapper = styled.aside`
    width: 100%;
    max-width: 760px;
    margin: 48px 0 0;
    padding: 24px 28px;
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    @media (max-width: 415px) {
        padding: 20px;
    }
`;

const Heading = styled.h2`
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 12px;
`;

const List = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Item = styled.li`
    font-size: 15px;
    line-height: 22px;
`;

const PeerLink = styled(Link)`
    color: #1268fb;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

interface RelatedInClusterProps {
    /** Limit number of peers shown. Defaults to 5. */
    limit?: number;
    /** Override the heading. Defaults to "More in {Cluster label}". */
    heading?: string;
}

/**
 * Auto-detects the current SEO route via useLocation and renders 3–5 sibling
 * pages from the same content cluster. Renders nothing on non-SEO routes.
 */
const RelatedInCluster: React.FC<RelatedInClusterProps> = ({ limit = 5, heading }) => {
    const location = useLocation();
    const meta = getSeoPageMeta(location.pathname);

    if (!meta) return null;

    const peers = getClusterPeers(location.pathname, limit);

    if (peers.length === 0) return null;

    const title = heading ?? `More in ${CLUSTERS[meta.cluster].label}`;

    return (
        <Wrapper aria-labelledby="related-in-cluster-heading">
            <Heading id="related-in-cluster-heading">{title}</Heading>
            <List>
                {peers.map(peer => (
                    <Item key={peer.path}>
                        <PeerLink to={peer.path}>{peer.label}</PeerLink>
                    </Item>
                ))}
            </List>
        </Wrapper>
    );
};

export default RelatedInCluster;
